/**
 * Copies a link to the clipboard so the Stippl app can pick it up on its first launch after an
 * install (used by the invite page: AppsFlyer's free plan no longer carries the invite deep link
 * through the App Store, so the app reads the clipboard instead). On Android the app reads it only
 * when Play's install referrer comes back `(not set)` (CU 123yb69552x); Android reads the plain text.
 *
 * The link must go on the pasteboard as `text/uri-list` as well as `text/plain`. WebKit maps
 * `text/uri-list` to the iOS URL pasteboard type, and the app first asks `UIPasteboard.hasURLs`
 * (silent, no "Allow Paste" prompt) and only reads the clipboard when that says yes. A plain-text-only
 * copy is invisible to that check.
 *
 * Call it synchronously from a click handler, before the navigation the click triggers: a user
 * gesture is required, and awaiting anything first loses it. Never throws. Returns whether the
 * synchronous copy succeeded; the async Clipboard API fallback fires and forgets, so its result is
 * not part of the return value.
 *
 * SSR: touches `document` / `navigator` only when called, never at import time.
 */

const noop = () => {};

/** `execCommand("copy")` with a `copy` listener that writes both formats. Returns whether the copy ran. */
const copyWithExecCommand = (url: string): boolean => {
	const previouslyFocused = document.activeElement as HTMLElement | null;
	const textarea = document.createElement("textarea");
	textarea.value = url;
	// readonly keeps iOS from opening the keyboard; off-screen + fixed keeps the page from scrolling.
	textarea.setAttribute("readonly", "");
	textarea.setAttribute("aria-hidden", "true");
	textarea.style.position = "fixed";
	textarea.style.top = "0";
	textarea.style.left = "-9999px";
	textarea.style.opacity = "0";

	const onCopy = (event: ClipboardEvent) => {
		// No clipboardData: leave the event alone and the browser copies the selection as plain text.
		if (!event.clipboardData) return;
		event.clipboardData.setData("text/uri-list", url);
		event.clipboardData.setData("text/plain", url);
		event.preventDefault();
	};

	try {
		document.addEventListener("copy", onCopy);
		document.body.appendChild(textarea);
		textarea.select();
		textarea.setSelectionRange(0, url.length); // iOS ignores select() alone
		return document.execCommand("copy");
	} catch {
		return false;
	} finally {
		document.removeEventListener("copy", onCopy);
		textarea.remove();
		try {
			if (previouslyFocused && previouslyFocused !== document.body) previouslyFocused.focus({ preventScroll: true });
		} catch {
			// Focus restore is best effort.
		}
	}
};

/** Fire-and-forget async Clipboard API write; every failure is swallowed. */
const copyWithAsyncClipboard = (url: string): void => {
	try {
		const clipboard = navigator.clipboard;
		if (!clipboard) return;
		const writeText = () => {
			try {
				Promise.resolve(clipboard.writeText?.(url)).catch(noop);
			} catch {
				// Best effort.
			}
		};
		if (clipboard.write && typeof window.ClipboardItem !== "undefined") {
			try {
				const item = new window.ClipboardItem({
					"text/plain": new Blob([url], { type: "text/plain" }),
					"text/uri-list": new Blob([url], { type: "text/uri-list" }),
				});
				// Some browsers reject formats they do not support: keep the plain-text copy as the last resort.
				Promise.resolve(clipboard.write([item])).catch(writeText);
				return;
			} catch {
				// ClipboardItem construction or write threw synchronously: plain text below.
			}
		}
		writeText();
	} catch {
		// No clipboard access at all (insecure context, old webview): nothing left to try.
	}
};

export const copyLinkForAppHandoff = (url: string): boolean => {
	if (!url) return false;
	let copied = false;
	try {
		copied = copyWithExecCommand(url);
	} catch {
		copied = false;
	}
	if (!copied) copyWithAsyncClipboard(url);
	return copied;
};
